import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Tab,
    Tabs,
    TableContainer,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {format, parseISO} from "date-fns";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowDown,
    faArrowUp,
    faBan,
    faCircleCheck,
    faCloudArrowDown,
    faCloudArrowUp,
    faCodeBranch,
    faCodeCommit,
    faCodeCompare,
    faCodeMerge,
    faRotateRight,
    faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {useApplicationContext} from "ApplicationContext";
import {CalculatorGitArea, GitConflictSide} from "apiClient/Api";
import {
    GitCommitInfo,
    GitConflictContent,
    GitFetchResult,
    GitFileDiff,
    GitFileState,
    GitPullResult,
    GitPushResult,
    GitStatus,
    normalizeConflict,
    normalizeDiffList,
} from "./gitTypes";

/** Remote-Operationen, die Anmeldedaten benötigen. */
type RemoteOperation = "fetch" | "pull" | "push";

/** Die Vorschau-Tabs unterhalb des Status. */
type PreviewTab = "history" | "incoming" | "outgoing";

const COLOR_BY_STATE: Record<GitFileState, "default" | "primary" | "secondary" | "success" | "warning" | "error" | "info"> = {
    Untracked: "default",
    Modified: "warning",
    Added: "success",
    Deleted: "error",
    Renamed: "info",
    Conflicted: "error",
};

/** Liest eine UI-taugliche Fehlermeldung aus einer Axios-/ProblemDetails-Antwort. */
const readError = (error: any, fallback: string): string =>
    error?.response?.data?.detail ??
    error?.response?.data?.title ??
    error?.message ??
    fallback;

/** Färbt eine Unified-Diff-Zeile nach Typ (Hunk/Hinzugefügt/Entfernt/Meta). */
const diffLineSx = (line: string) => {
    if (line.startsWith("@@")) return { color: "info.main", bgcolor: "action.hover" };
    if (line.startsWith("+++") || line.startsWith("---") || line.startsWith("diff ") || line.startsWith("index "))
        return { color: "text.secondary" };
    if (line.startsWith("+")) return { color: "success.main", bgcolor: "rgba(46,160,67,0.14)" };
    if (line.startsWith("-")) return { color: "error.main", bgcolor: "rgba(248,81,73,0.14)" };
    return {};
};

/** Rendert einen Unified-Diff-Text zeilenweise eingefärbt. */
const DiffLines: React.FC<{ diff: string }> = ({ diff }) => (
    <Box sx={{ m: 0, fontFamily: "monospace", fontSize: 12, overflowX: "auto" }}>
        {diff.split("\n").map((line, i) => (
            <Box key={i} component="div" sx={{ px: 1, whiteSpace: "pre", ...diffLineSx(line) }}>
                {line || " "}
            </Box>
        ))}
    </Box>
);

/** Zeigt eine Liste von Datei-Diffs (Working- oder Commit-Diff). */
const DiffView: React.FC<{ files: GitFileDiff[] }> = ({ files }) => {
    if (files.length === 0) return <Typography color="text.secondary">Keine Änderungen.</Typography>;
    return (
        <Stack spacing={2}>
            {files.map((f, i) => (
                <Box key={f.path || i} sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                    <Stack direction="row" sx={{ alignItems: "center", gap: 1, p: 1, bgcolor: "action.hover" }}>
                        <Typography sx={{ fontFamily: "monospace", fontWeight: 600, flexGrow: 1, wordBreak: "break-all" }}>
                            {f.path}
                        </Typography>
                        {f.change && <Chip size="small" label={f.change} />}
                        {f.additions > 0 && (
                            <Typography variant="caption" sx={{ color: "success.main" }}>+{f.additions}</Typography>
                        )}
                        {f.deletions > 0 && (
                            <Typography variant="caption" sx={{ color: "error.main" }}>−{f.deletions}</Typography>
                        )}
                    </Stack>
                    {f.isBinary ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            Binärdatei – kein Text-Diff.
                        </Typography>
                    ) : f.diff ? (
                        <DiffLines diff={f.diff} />
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            Kein Diff-Inhalt.
                        </Typography>
                    )}
                </Box>
            ))}
        </Stack>
    );
};

/** 3-Wege-Ansicht einer Konfliktdatei (Basis / Ours / Theirs). */
const ConflictView: React.FC<{ content: GitConflictContent }> = ({ content }) => (
    <Stack spacing={2}>
        {(
            [
                ["Basis (gemeinsamer Stand)", content.base],
                ["Unsere Änderung (Ours)", content.ours],
                ["Eingehende Änderung (Theirs)", content.theirs],
            ] as const
        ).map(([label, text]) => (
            <Box key={label} sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                <Typography variant="subtitle2" sx={{ p: 1, bgcolor: "action.hover" }}>
                    {label}
                </Typography>
                <Box
                    component="pre"
                    sx={{ m: 0, p: 1, fontFamily: "monospace", fontSize: 12, maxHeight: 300, overflow: "auto", whiteSpace: "pre-wrap" }}
                >
                    {text || "—"}
                </Box>
            </Box>
        ))}
    </Stack>
);

/**
 * Git-Steuerung der beiden Kalkulator-Datenbereiche (Office / Artikel): Status,
 * Commit, Fetch/Pull/Push, Commit-Vorschau und Konflikt-Auflösung. Nur Admin+.
 *
 * Einbettbar — wird als Tab in der Stammdaten-Seite gerendert, daher ohne
 * eigenen Seitentitel.
 */
export const CalculatorGitContent: React.FC = () => {
    const {t} = useTranslation();
    const [area, setArea] = useState<CalculatorGitArea>(CalculatorGitArea.Office);

    return (
        <Box sx={{width: "100%"}}>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                {t("calculatorGit.subtitle")}
            </Typography>

            <Paper sx={{mb: 2}}>
                <Tabs value={area} onChange={(_e, value) => setArea(value)}>
                    <Tab value={CalculatorGitArea.Office} label={t("calculatorGit.areas.office")}/>
                    <Tab value={CalculatorGitArea.Artikel} label={t("calculatorGit.areas.artikel")}/>
                </Tabs>
            </Paper>

            {/* key erzwingt frischen State je Bereich (eigener Status/Verlauf). */}
            <GitAreaPanel key={area} area={area}/>
        </Box>
    );
};

/** Steuerung eines einzelnen Datenbereichs. */
const GitAreaPanel: React.FC<{area: CalculatorGitArea}> = ({area}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();

    const [status, setStatus] = useState<GitStatus | undefined>(undefined);
    const [history, setHistory] = useState<GitCommitInfo[]>([]);
    const [incoming, setIncoming] = useState<GitCommitInfo[]>([]);
    const [outgoing, setOutgoing] = useState<GitCommitInfo[]>([]);

    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [loadError, setLoadError] = useState<string | undefined>(undefined);
    const [previewTab, setPreviewTab] = useState<PreviewTab>("history");

    const [commitOpen, setCommitOpen] = useState(false);
    const [commitMessage, setCommitMessage] = useState("");

    const [credentialOperation, setCredentialOperation] = useState<RemoteOperation | undefined>(undefined);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [credentialError, setCredentialError] = useState<string | undefined>(undefined);
    const [credentialSubmitting, setCredentialSubmitting] = useState(false);

    // Diff-/Konflikt-Viewer
    const [diffOpen, setDiffOpen] = useState(false);
    const [diffTitle, setDiffTitle] = useState("");
    const [diffFiles, setDiffFiles] = useState<GitFileDiff[] | null>(null); // null = lädt
    const [conflictOpen, setConflictOpen] = useState(false);
    const [conflictPath, setConflictPath] = useState("");
    const [conflictContent, setConflictContent] = useState<GitConflictContent | null>(null);

    const gitApi = context.api.calculator;

    const refresh = useCallback(async () => {
        setLoading(true);
        setLoadError(undefined);
        try {
            const [statusResult, historyResult, incomingResult, outgoingResult] = await Promise.all([
                gitApi.calculatorGitGetStatus(area),
                gitApi.calculatorGitGetLog(area, {max: 50}),
                gitApi.calculatorGitGetIncoming(area, {max: 100}),
                gitApi.calculatorGitGetOutgoing(area, {max: 100}),
            ]);
            setStatus(statusResult.data as GitStatus);
            setHistory((historyResult.data as GitCommitInfo[]) ?? []);
            setIncoming((incomingResult.data as GitCommitInfo[]) ?? []);
            setOutgoing((outgoingResult.data as GitCommitInfo[]) ?? []);
        } catch (error: any) {
            setStatus(undefined);
            setLoadError(readError(error, t("calculatorGit.errors.load")));
        } finally {
            setLoading(false);
        }
    }, [area, gitApi, t]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const commit = async () => {
        const message = commitMessage.trim();
        if (!message) return;
        setBusy(true);
        try {
            const response = await gitApi.calculatorGitCommit(area, {message});
            if (response.status === 204) {
                enqueueSnackbar(t("calculatorGit.feedback.nothingToCommit"), {variant: "info"});
            } else {
                enqueueSnackbar(t("calculatorGit.feedback.committed"), {variant: "success"});
            }
            setCommitOpen(false);
            setCommitMessage("");
            await refresh();
        } catch (error: any) {
            enqueueSnackbar(readError(error, t("calculatorGit.errors.commit")), {variant: "error"});
        } finally {
            setBusy(false);
        }
    };

    const runRemoteOperation = async () => {
        if (!credentialOperation) return;
        if (!username.trim() || !password) {
            setCredentialError(t("calculatorGit.credentials.required"));
            return;
        }
        const credentials = {username: username.trim(), password};
        setCredentialSubmitting(true);
        setCredentialError(undefined);
        try {
            if (credentialOperation === "fetch") {
                const result = (await gitApi.calculatorGitFetch(area, credentials)).data as GitFetchResult;
                enqueueSnackbar(
                    t("calculatorGit.feedback.fetched", {behind: result.behindBy}),
                    {variant: result.behindBy > 0 ? "info" : "success"},
                );
            } else if (credentialOperation === "pull") {
                const result = (await gitApi.calculatorGitPull(area, credentials)).data as GitPullResult;
                if (result.outcome === "Conflicts") {
                    enqueueSnackbar(t("calculatorGit.feedback.pullConflicts"), {variant: "warning"});
                } else {
                    enqueueSnackbar(
                        t("calculatorGit.feedback.pulled", {outcome: t(`calculatorGit.mergeOutcome.${result.outcome}`)}),
                        {variant: "success"},
                    );
                }
            } else {
                const result = (await gitApi.calculatorGitPush(area, credentials)).data as GitPushResult;
                if (result.success) {
                    enqueueSnackbar(t("calculatorGit.feedback.pushed"), {variant: "success"});
                } else {
                    enqueueSnackbar(
                        t("calculatorGit.feedback.pushRejected", {reason: result.rejectReason ?? "—"}),
                        {variant: "error"},
                    );
                }
            }
            closeCredentialDialog();
            await refresh();
        } catch (error: any) {
            // 401 → Anmeldedaten falsch: Dialog offen lassen, Meldung anzeigen.
            if (error?.response?.status === 401) {
                setCredentialError(t("calculatorGit.credentials.invalid"));
            } else {
                setCredentialError(readError(error, t("calculatorGit.errors.remote")));
            }
        } finally {
            setCredentialSubmitting(false);
        }
    };

    const resolveConflict = async (path: string, side: GitConflictSide) => {
        setBusy(true);
        try {
            const response = await gitApi.calculatorGitResolve(area, {path, side});
            setStatus(response.data as GitStatus);
            enqueueSnackbar(t("calculatorGit.feedback.resolved", {path}), {variant: "success"});
            await refresh();
        } catch (error: any) {
            enqueueSnackbar(readError(error, t("calculatorGit.errors.resolve")), {variant: "error"});
        } finally {
            setBusy(false);
        }
    };

    const abortMerge = async () => {
        setBusy(true);
        try {
            const response = await gitApi.calculatorGitAbortMerge(area);
            setStatus(response.data as GitStatus);
            enqueueSnackbar(t("calculatorGit.feedback.mergeAborted"), {variant: "success"});
            await refresh();
        } catch (error: any) {
            enqueueSnackbar(readError(error, t("calculatorGit.errors.abort")), {variant: "error"});
        } finally {
            setBusy(false);
        }
    };

    const openWorkingDiff = async (path?: string) => {
        setDiffTitle(path ?? t("calculatorGit.diff.workingTitle", "Nicht committete Änderungen"));
        setDiffFiles(null);
        setDiffOpen(true);
        try {
            const res = await gitApi.calculatorGitGetWorkingDiff(area, path ? { path } : undefined);
            setDiffFiles(normalizeDiffList(res.data));
        } catch (error: any) {
            setDiffFiles([]);
            enqueueSnackbar(readError(error, t("calculatorGit.errors.diff", "Diff konnte nicht geladen werden.")), {
                variant: "error",
            });
        }
    };

    const openCommitDiff = async (sha: string, label: string) => {
        setDiffTitle(label);
        setDiffFiles(null);
        setDiffOpen(true);
        try {
            const res = await gitApi.calculatorGitGetCommitDiff(area, sha);
            setDiffFiles(normalizeDiffList(res.data));
        } catch (error: any) {
            setDiffFiles([]);
            enqueueSnackbar(readError(error, t("calculatorGit.errors.diff", "Diff konnte nicht geladen werden.")), {
                variant: "error",
            });
        }
    };

    const openConflict = async (path: string) => {
        setConflictPath(path);
        setConflictContent(null);
        setConflictOpen(true);
        try {
            const res = await gitApi.calculatorGitGetConflict(area, { path });
            setConflictContent(normalizeConflict(res.data));
        } catch (error: any) {
            setConflictContent(normalizeConflict({}));
            enqueueSnackbar(readError(error, t("calculatorGit.errors.conflict", "Konflikt konnte nicht geladen werden.")), {
                variant: "error",
            });
        }
    };

    const openCredentialDialog = (operation: RemoteOperation) => {
        setCredentialOperation(operation);
        setUsername("");
        setPassword("");
        setCredentialError(undefined);
    };

    const closeCredentialDialog = () => {
        setCredentialOperation(undefined);
        setUsername("");
        setPassword("");
        setCredentialError(undefined);
    };

    const formatWhen = (iso: string | null | undefined): string =>
        iso ? format(parseISO(iso), "dd.MM.yyyy HH:mm") : "";

    const commitColumns: GridColDef<GitCommitInfo>[] = useMemo(() => [
        {
            field: "when",
            headerName: t("calculatorGit.columns.when"),
            width: 150,
            valueFormatter: (value: string | undefined) => formatWhen(value),
        },
        {field: "author", headerName: t("calculatorGit.columns.author"), width: 180},
        {field: "messageShort", headerName: t("calculatorGit.columns.message"), flex: 1, minWidth: 240},
        {
            field: "sha",
            headerName: t("calculatorGit.columns.sha"),
            width: 100,
            valueFormatter: (value: string | undefined) => (value ?? "").substring(0, 7),
        },
    ], [t]);

    const hasChanges = (status?.changes.length ?? 0) > 0;
    const hasConflicts = status?.hasConflicts ?? false;
    const disabled = busy || loading;

    const previewRows = previewTab === "incoming" ? incoming : previewTab === "outgoing" ? outgoing : history;

    return (
        <Box>
            <Stack direction="row" sx={{justifyContent: "flex-end", mb: 2}}>
                <Button
                    onClick={refresh}
                    startIcon={<FontAwesomeIcon icon={faRotateRight}/>}
                    variant="outlined"
                    disabled={disabled}
                >
                    {t("calculatorGit.actions.refresh")}
                </Button>
            </Stack>

            {loadError && (
                <Alert severity="error" sx={{mb: 2}}>{loadError}</Alert>
            )}

            {status && (
                <Paper sx={{p: 2, mb: 2}}>
                    <Stack direction="row" sx={{flexWrap: "wrap", gap: 1, alignItems: "center"}}>
                        <Chip
                            icon={<FontAwesomeIcon icon={faCodeBranch}/>}
                            label={status.branch}
                            color="primary"
                            variant="outlined"
                        />
                        {status.upstream && (
                            <Typography variant="body2" color="text.secondary">
                                {t("calculatorGit.upstream", {upstream: status.upstream})}
                            </Typography>
                        )}
                        <Box sx={{flexGrow: 1}}/>
                        {status.aheadBy > 0 && (
                            <Tooltip title={t("calculatorGit.aheadTooltip")}>
                                <Chip
                                    size="small"
                                    color="info"
                                    icon={<FontAwesomeIcon icon={faArrowUp}/>}
                                    label={status.aheadBy}
                                />
                            </Tooltip>
                        )}
                        {status.behindBy > 0 && (
                            <Tooltip title={t("calculatorGit.behindTooltip")}>
                                <Chip
                                    size="small"
                                    color="warning"
                                    icon={<FontAwesomeIcon icon={faArrowDown}/>}
                                    label={status.behindBy}
                                />
                            </Tooltip>
                        )}
                        {status.isClean ? (
                            <Chip
                                size="small"
                                color="success"
                                icon={<FontAwesomeIcon icon={faCircleCheck}/>}
                                label={t("calculatorGit.clean")}
                            />
                        ) : (
                            <Chip
                                size="small"
                                color="default"
                                label={t("calculatorGit.changeCount", {count: status.changes.length})}
                            />
                        )}
                        {hasConflicts && (
                            <Chip
                                size="small"
                                color="error"
                                icon={<FontAwesomeIcon icon={faTriangleExclamation}/>}
                                label={t("calculatorGit.conflictCount", {count: status.conflicts.length})}
                            />
                        )}
                    </Stack>

                    <Divider sx={{my: 2}}/>

                    <Stack direction="row" sx={{flexWrap: "wrap", gap: 1}}>
                        <Button
                            variant="contained"
                            startIcon={<FontAwesomeIcon icon={faCodeCommit}/>}
                            onClick={() => setCommitOpen(true)}
                            disabled={disabled || !hasChanges || hasConflicts}
                        >
                            {t("calculatorGit.actions.commit")}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FontAwesomeIcon icon={faCloudArrowDown}/>}
                            onClick={() => openCredentialDialog("fetch")}
                            disabled={disabled}
                        >
                            {t("calculatorGit.actions.fetch")}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FontAwesomeIcon icon={faCodeMerge}/>}
                            onClick={() => openCredentialDialog("pull")}
                            disabled={disabled || hasConflicts}
                        >
                            {t("calculatorGit.actions.pull")}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FontAwesomeIcon icon={faCloudArrowUp}/>}
                            onClick={() => openCredentialDialog("push")}
                            disabled={disabled || hasConflicts}
                        >
                            {t("calculatorGit.actions.push")}
                        </Button>
                        {hasConflicts && (
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<FontAwesomeIcon icon={faBan}/>}
                                onClick={abortMerge}
                                disabled={disabled}
                            >
                                {t("calculatorGit.actions.abortMerge")}
                            </Button>
                        )}
                    </Stack>
                </Paper>
            )}

            {status && hasConflicts && (
                <Alert severity="error" sx={{mb: 2}}>
                    <Typography variant="subtitle2" sx={{mb: 1}}>
                        {t("calculatorGit.conflicts.title")}
                    </Typography>
                    <List dense disablePadding>
                        {status.conflicts.map(path => (
                            <ListItem
                                key={path}
                                disableGutters
                                secondaryAction={
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            size="small"
                                            startIcon={<FontAwesomeIcon icon={faCodeCompare}/>}
                                            onClick={() => openConflict(path)}
                                            disabled={disabled}
                                        >
                                            {t("calculatorGit.conflicts.compare", "3-Wege")}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => resolveConflict(path, GitConflictSide.Ours)}
                                            disabled={disabled}
                                        >
                                            {t("calculatorGit.conflicts.keepOurs")}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => resolveConflict(path, GitConflictSide.Theirs)}
                                            disabled={disabled}
                                        >
                                            {t("calculatorGit.conflicts.takeTheirs")}
                                        </Button>
                                    </Stack>
                                }
                            >
                                <ListItemText primary={path} slotProps={{primary: {sx: {fontFamily: "monospace"}}}}/>
                            </ListItem>
                        ))}
                    </List>
                </Alert>
            )}

            {status && hasChanges && (
                <Paper sx={{p: 2, mb: 2}}>
                    <Stack direction="row" sx={{alignItems: "center", justifyContent: "space-between", mb: 1}}>
                        <Typography variant="subtitle2">
                            {t("calculatorGit.changes.title")}
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<FontAwesomeIcon icon={faCodeCompare}/>}
                            onClick={() => openWorkingDiff()}
                            disabled={disabled}
                        >
                            {t("calculatorGit.diff.viewAll", "Alle Änderungen ansehen")}
                        </Button>
                    </Stack>
                    <List dense disablePadding sx={{maxHeight: 280, overflow: "auto"}}>
                        {status.changes.map(change => (
                            <ListItem
                                key={change.path}
                                disableGutters
                                secondaryAction={
                                    <Button size="small" onClick={() => openWorkingDiff(change.path)} disabled={disabled}>
                                        {t("calculatorGit.diff.view", "Diff")}
                                    </Button>
                                }
                            >
                                <Chip
                                    size="small"
                                    color={COLOR_BY_STATE[change.state]}
                                    label={t(`calculatorGit.fileState.${change.state}`)}
                                    sx={{mr: 1, minWidth: 96}}
                                />
                                <ListItemText
                                    primary={change.path}
                                    secondary={change.staged ? t("calculatorGit.changes.staged") : undefined}
                                    slotProps={{primary: {sx: {fontFamily: "monospace"}}}}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            <Paper>
                <Tabs value={previewTab} onChange={(_e, value) => setPreviewTab(value)} sx={{px: 1}}>
                    <Tab value="history" label={t("calculatorGit.preview.history")}/>
                    <Tab value="incoming" label={t("calculatorGit.preview.incoming", {count: incoming.length})}/>
                    <Tab value="outgoing" label={t("calculatorGit.preview.outgoing", {count: outgoing.length})}/>
                </Tabs>
                <TableContainer>
                    <DataGrid
                        rows={previewRows}
                        columns={commitColumns}
                        loading={loading}
                        autoHeight
                        getRowId={row => row.sha}
                        density="compact"
                        hideFooterSelectedRowCount
                        onRowClick={params => openCommitDiff(params.row.sha, params.row.messageShort)}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{pagination: {paginationModel: {pageSize: 10}}}}
                        sx={{"& .MuiDataGrid-row": {cursor: "pointer"}}}
                    />
                </TableContainer>
            </Paper>

            {/* Commit-Dialog */}
            <Dialog open={commitOpen} onClose={() => !busy && setCommitOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{t("calculatorGit.commitDialog.title")}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        {t("calculatorGit.commitDialog.body")}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        label={t("calculatorGit.commitDialog.message")}
                        value={commitMessage}
                        onChange={e => setCommitMessage(e.target.value)}
                        disabled={busy}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCommitOpen(false)} color="inherit" disabled={busy}>
                        {t("calculatorGit.commitDialog.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={commit}
                        disabled={busy || !commitMessage.trim()}
                        startIcon={busy ? <CircularProgress size={16} color="inherit"/> : undefined}
                    >
                        {t("calculatorGit.commitDialog.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Anmeldedaten-Dialog für fetch/pull/push */}
            <Dialog
                open={credentialOperation !== undefined}
                onClose={() => !credentialSubmitting && closeCredentialDialog()}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>
                    {credentialOperation && t(`calculatorGit.credentials.title.${credentialOperation}`)}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        {t("calculatorGit.credentials.body")}
                    </DialogContentText>
                    {credentialError && (
                        <Alert severity="error" sx={{mb: 2}}>{credentialError}</Alert>
                    )}
                    <Stack
                        component="form"
                        spacing={2}
                        onSubmit={e => {
                            e.preventDefault();
                            runRemoteOperation();
                        }}
                    >
                        <TextField
                            autoFocus
                            fullWidth
                            label={t("calculatorGit.credentials.username")}
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            autoComplete="username"
                            disabled={credentialSubmitting}
                        />
                        <TextField
                            fullWidth
                            type="password"
                            label={t("calculatorGit.credentials.password")}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            autoComplete="current-password"
                            disabled={credentialSubmitting}
                        />
                        {/* Verstecktes Submit, damit Enter im Formular auslöst. */}
                        <button type="submit" style={{display: "none"}} aria-hidden/>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCredentialDialog} color="inherit" disabled={credentialSubmitting}>
                        {t("calculatorGit.credentials.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={runRemoteOperation}
                        disabled={credentialSubmitting || !username.trim() || !password}
                        startIcon={credentialSubmitting ? <CircularProgress size={16} color="inherit"/> : undefined}
                    >
                        {t("calculatorGit.credentials.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diff-Viewer (Working- bzw. Commit-Diff) */}
            <Dialog open={diffOpen} onClose={() => setDiffOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle sx={{wordBreak: "break-all"}}>
                    {t("calculatorGit.diff.title", "Diff")}: {diffTitle}
                </DialogTitle>
                <DialogContent dividers>
                    {diffFiles === null ? (
                        <Stack sx={{alignItems: "center", py: 4}}>
                            <CircularProgress/>
                        </Stack>
                    ) : (
                        <DiffView files={diffFiles}/>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDiffOpen(false)}>{t("calculatorGit.diff.close", "Schließen")}</Button>
                </DialogActions>
            </Dialog>

            {/* 3-Wege-Konflikt-Ansicht */}
            <Dialog open={conflictOpen} onClose={() => setConflictOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle sx={{wordBreak: "break-all"}}>
                    {t("calculatorGit.conflicts.compareTitle", "Konflikt")}: {conflictPath}
                </DialogTitle>
                <DialogContent dividers>
                    {conflictContent === null ? (
                        <Stack sx={{alignItems: "center", py: 4}}>
                            <CircularProgress/>
                        </Stack>
                    ) : (
                        <ConflictView content={conflictContent}/>
                    )}
                </DialogContent>
                <DialogActions sx={{justifyContent: "space-between", px: 3}}>
                    <Button onClick={() => setConflictOpen(false)} color="inherit">
                        {t("calculatorGit.diff.close", "Schließen")}
                    </Button>
                    <Box>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                resolveConflict(conflictPath, GitConflictSide.Ours);
                                setConflictOpen(false);
                            }}
                            disabled={disabled}
                            sx={{mr: 1}}
                        >
                            {t("calculatorGit.conflicts.keepOurs")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                resolveConflict(conflictPath, GitConflictSide.Theirs);
                                setConflictOpen(false);
                            }}
                            disabled={disabled}
                        >
                            {t("calculatorGit.conflicts.takeTheirs")}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CalculatorGitContent;
